import {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  getBuybackFeedback,
  submitBuybackFeedback,
} from '../../../shared/services/apiGateway';
import checkApiErrorResponse from '../../../shared/services/checkApiErrorResponse';

const defaultErrors = {
  message: null,
  statusCode: null,
};

const VALID_RESPONSE_TYPES = new Set([
  'DECLINE',
  'PRO_RATA',
  'PRO_RATA_2X',
  'MAX',
]);

const isEmptyFeedback = (response) => {
  if (!response || typeof response !== 'object') {
    return true;
  }

  const {
    responseType,
    sellRequests,
    notes,
    requestedAt,
    createdAt,
    updatedAt,
  } = response;
  const hasResponseType = typeof responseType === 'string' && VALID_RESPONSE_TYPES.has(responseType.trim());
  const hasSellRequests = Array.isArray(sellRequests) && sellRequests.length > 0;
  const hasNotes = typeof notes === 'string' && notes.trim().length > 0;
  const hasTimelineMetadata = [requestedAt, createdAt, updatedAt].some(
    (value) => typeof value === 'string' && value.trim().length > 0,
  );

  const hasMeaningfulFeedback = (hasResponseType && hasTimelineMetadata)
    || hasSellRequests
    || hasNotes;

  return !hasMeaningfulFeedback;
};

const useBuybackFeedback = (userid = '', options = {}) => {
  const { enabled = true } = options;
  const [feedback, setFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setFeedback(null);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    const abortController = new AbortController();
    let ignore = false;

    const loadFeedback = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getBuybackFeedback(userid, abortController.signal);
        if (ignore || response === 'aborted') {
          return;
        }

        if (response && response.statusCode === 404) {
          setFeedback(null);
          return;
        }

        if (checkApiErrorResponse(response)) {
          setError(response || defaultErrors);
          return;
        }

        if (
          !response
          || (typeof response === 'object' && Object.keys(response).length === 0)
          || isEmptyFeedback(response)
        ) {
          setFeedback(null);
          return;
        }

        setFeedback(response);
      } catch (err) {
        if (!ignore && err !== 'aborted') {
          setError(err);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    loadFeedback();

    return () => {
      ignore = true;
      abortController.abort();
    };
  }, [userid, refreshKey, enabled]);

  const refresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const clearSubmitError = useCallback(() => {
    setSubmitError(null);
  }, []);

  const submit = useCallback(
    async (payload) => {
      if (!enabled) {
        return null;
      }

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const response = await submitBuybackFeedback(payload, userid);

        if (checkApiErrorResponse(response)) {
          setSubmitError(response || defaultErrors);
          return null;
        }

        if (isEmptyFeedback(response)) {
          setFeedback(null);
        } else {
          setFeedback(response);
        }
        refresh();
        return response;
      } catch (err) {
        setSubmitError(err);
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [userid, refresh, enabled],
  );

  return {
    feedback,
    isLoading,
    error,
    refresh,
    submit,
    isSubmitting,
    submitError,
    clearSubmitError,
  };
};

export default useBuybackFeedback;
